import { useState, useEffect, useCallback } from "react";

interface SplashScreenProps {
  onComplete: () => void;
}

const GOLD = "#C9A96E";

export default function SplashScreen({ onComplete }: SplashScreenProps) {
  const [phase, setPhase] = useState<"in" | "hold" | "exit" | "done">("in");

  const finish = useCallback(() => {
    setPhase("done");
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    const exitTimer  = setTimeout(() => setPhase("exit"), 1900);
    const doneTimer  = setTimeout(finish, 2800);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [finish]);

  if (phase === "done") return null;

  const isExiting = phase === "exit";

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden"
      aria-hidden="true"
    >
      {/* ── Ambient radial glow — barely visible, just premium depth ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,169,110,0.08) 0%, transparent 70%)",
          animation: "splash-glow-pulse 2s ease-in-out infinite",
        }}
      />

      {/* ── TOP CURTAIN ── */}
      <div
        className="absolute inset-x-0 top-0 h-1/2"
        style={{
          background: "#060606",
          animation: isExiting
            ? "splash-curtain-up 0.9s cubic-bezier(0.76, 0, 0.24, 1) forwards"
            : "none",
        }}
      />

      {/* ── BOTTOM CURTAIN ── */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background: "#060606",
          animation: isExiting
            ? "splash-curtain-down 0.9s cubic-bezier(0.76, 0, 0.24, 1) forwards"
            : "none",
        }}
      />

      {/* ── CENTER LOGO ── */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center select-none"
        style={{
          animation: isExiting
            ? "splash-logo-out 0.35s ease-in forwards"
            : "none",
        }}
      >
        {/* Top gold line — expands from center */}
        <div
          style={{
            width: "56px",
            height: "1px",
            background: `linear-gradient(90deg, transparent 0%, ${GOLD} 30%, ${GOLD} 70%, transparent 100%)`,
            marginBottom: "32px",
            transformOrigin: "center",
            animation: "splash-line-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both",
          }}
        />

        {/* SPDB — oversized, cinematic rise from bottom */}
        <div style={{ overflow: "hidden", lineHeight: 1 }}>
          <h1
            className="font-serif text-white font-medium"
            style={{
              fontSize: "clamp(80px, 16vw, 160px)",
              letterSpacing: "0.14em",
              animation:
                "splash-title-rise 1s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both",
            }}
          >
            SPDB
          </h1>
        </div>

        {/* STUDIO — wide tracked, fades up */}
        <p
          className="font-sans uppercase"
          style={{
            fontSize: "10px",
            letterSpacing: "0.58em",
            color: "rgba(255,255,255,0.35)",
            marginTop: "10px",
            animation: "splash-fade-slide 0.65s ease-out 0.85s both",
          }}
        >
          STUDIO
        </p>

        {/* Tagline — gold italic, subtle */}
        <p
          className="font-serif italic"
          style={{
            fontSize: "13px",
            color: GOLD,
            marginTop: "22px",
            animation: "splash-tagline-in 0.7s ease-out 1.1s both",
          }}
        >
          Vom Inserat zur Ikone.
        </p>

        {/* Bottom gold line — mirrored */}
        <div
          style={{
            width: "56px",
            height: "1px",
            background: `linear-gradient(90deg, transparent 0%, ${GOLD} 30%, ${GOLD} 70%, transparent 100%)`,
            marginTop: "32px",
            transformOrigin: "center",
            animation: "splash-line-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
          }}
        />
      </div>
    </div>
  );
}
